import _slicedToArray from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import { Loader } from '@infl-ts/ui-advocate-components';
import { borderRadius as _borderRadius } from '@infl-ts/ui-advocate-theme';
import { DE$ } from '@infl-ts/util-datum';
import { useToggle } from '@infl-ts/util-hooks';
import { logQueryError } from '@infl-ts/util-query-errors';
import CloseIcon from '@mui/icons-material/Close';
import ThumbIcon from '@mui/icons-material/ThumbUp';
import { Box, Button, Dialog, DialogTitle, Skeleton } from '@mui/material';
import * as DE from '@nll/datum/lib/DatumEither';
import * as A from 'fp-ts/Array';
import { constNull, flow, pipe } from 'fp-ts/function';
import * as O from 'fp-ts/Option';
import { Translation, useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import LikeItem from './like-item';
import { useLikes, useProfileId } from './queries';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";

var LikesPlaceholder = function LikesPlaceholder() {
  return /*#__PURE__*/_jsx(Box, {
    sx: {
      width: 'inherit'
    },
    "data-element": "Box",
    "data-component": "LikesPlaceholder",
    "data-source-file": "likes-list.tsx",
    children: A.makeBy(10, function (i) {
      return /*#__PURE__*/_jsxs(Box, {
        sx: {
          mx: 3,
          mb: 2,
          display: 'flex',
          width: 1,
          alignItems: 'center'
        },
        children: [/*#__PURE__*/_jsx(Skeleton, {
          variant: "circular",
          width: 81,
          height: 40
        }), /*#__PURE__*/_jsx(Skeleton, {
          variant: "rectangular",
          sx: {
            ml: 1,
            borderRadius: function borderRadius(t) {
              return _borderRadius(t);
            }
          },
          height: 24,
          width: "100%"
        }), /*#__PURE__*/_jsx(Skeleton, {
          variant: "circular",
          sx: {
            ml: 7
          },
          height: 20,
          width: 38
        }), /*#__PURE__*/_jsx(Skeleton, {
          variant: "circular",
          sx: {
            ml: 1
          },
          height: 20,
          width: 38
        })]
      }, i);
    })
  });
};

var DialogHeader = function DialogHeader(_ref) {
  var handleCloseDialog = _ref.handleCloseDialog;
  return /*#__PURE__*/_jsxs(DialogTitle, {
    variant: "h3",
    sx: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    "data-element": "DialogTitle",
    "data-component": "DialogHeader",
    "data-source-file": "likes-list.tsx",
    children: [/*#__PURE__*/_jsx(Translation, {
      "data-element": "Translation",
      "data-component": "DialogHeader",
      "data-source-file": "likes-list.tsx",
      children: function children(t) {
        return t('Liked by');
      }
    }), /*#__PURE__*/_jsx(CloseIcon, {
      sx: {
        cursor: 'pointer'
      },
      onClick: handleCloseDialog,
      "data-element": "CloseIcon",
      "data-source-file": "likes-list.tsx"
    })]
  });
};

var LikesButton = function LikesButton(_ref2) {
  var showDialog = _ref2.showDialog,
      totalCount = _ref2.totalCount;
  return /*#__PURE__*/_jsx(Button, {
    size: "small",
    onClick: showDialog,
    startIcon: /*#__PURE__*/_jsx(ThumbIcon, {
      sx: {
        color: function color(t) {
          return t.palette.neutral.light;
        },
        width: 16,
        height: 16
      }
    }),
    sx: {
      color: function color(t) {
        return t.palette.text.secondary;
      },
      fontWeight: function fontWeight(t) {
        return t.typography.fontWeightBold;
      },
      minWidth: 'auto',
      px: 1
    },
    "data-element": "Button",
    "data-component": "LikesButton",
    "data-source-file": "likes-list.tsx",
    children: totalCount
  });
};

var LikesDialog = function LikesDialog(_ref3) {
  var entityId = _ref3.entityId,
      handleCloseDialog = _ref3.handleCloseDialog;
  var profileId = pipe(useProfileId(), DE.map(function (_ref4) {
    var id = _ref4.profile.id;
    return id;
  }), DE$.getSuccessOrElse(null));

  var _useLikes = useLikes({
    variables: {
      id: entityId
    },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true
  }),
      result = _useLikes.result,
      fetchMore = _useLikes.fetchMore;

  var handleFetchMore = function handleFetchMore(likesCursor) {
    return fetchMore({
      variables: {
        likesCursor: likesCursor
      }
    });
  };

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  return /*#__PURE__*/_jsxs(Dialog, {
    open: true,
    onClose: handleCloseDialog,
    fullWidth: true,
    scroll: "paper",
    PaperProps: {
      id: 'likesDialogPaper',
      sx: {
        maxHeight: {
          xs: 'auto',
          md: '50%'
        }
      }
    },
    "data-element": "Dialog",
    "data-component": "LikesDialog",
    "data-source-file": "likes-list.tsx",
    children: [/*#__PURE__*/_jsx(DialogHeader, {
      handleCloseDialog: handleCloseDialog,
      "data-element": "DialogHeader",
      "data-component": "LikesDialog",
      "data-source-file": "likes-list.tsx"
    }), pipe(result, DE.squash(function () {
      return /*#__PURE__*/_jsx(LikesPlaceholder, {});
    }, flow(logQueryError, function () {
      return /*#__PURE__*/_jsx(Box, {
        sx: {
          my: 2,
          mx: 3
        },
        children: t('This has been deleted. Please refresh the page')
      });
    }), function (_ref5) {
      var _likes$edges$length, _likes$edges, _likes$pageInfo2;

      var likes = _ref5.node.likes;
      return /*#__PURE__*/_jsx(InfiniteScroll, {
        scrollableTarget: 'likesDialogPaper',
        dataLength: (_likes$edges$length = likes === null || likes === void 0 ? void 0 : (_likes$edges = likes.edges) === null || _likes$edges === void 0 ? void 0 : _likes$edges.length) !== null && _likes$edges$length !== void 0 ? _likes$edges$length : 0,
        next: function next() {
          var _likes$pageInfo$endCu, _likes$pageInfo;

          return handleFetchMore((_likes$pageInfo$endCu = likes === null || likes === void 0 ? void 0 : (_likes$pageInfo = likes.pageInfo) === null || _likes$pageInfo === void 0 ? void 0 : _likes$pageInfo.endCursor) !== null && _likes$pageInfo$endCu !== void 0 ? _likes$pageInfo$endCu : '0');
        },
        hasMore: !!(likes !== null && likes !== void 0 && (_likes$pageInfo2 = likes.pageInfo) !== null && _likes$pageInfo2 !== void 0 && _likes$pageInfo2.hasNextPage),
        loader: /*#__PURE__*/_jsx(Loader, {}),
        children: pipe(likes === null || likes === void 0 ? void 0 : likes.edges, O.fromNullable, O.chain(O.fromPredicate(A.isNonEmpty)), O.match(constNull, A.map(function (_ref6) {
          var node = _ref6.node;
          return pipe(profileId && "member_".concat(profileId) === node.member.id, function (belongsToCurrentUser) {
            return /*#__PURE__*/_jsx(LikeItem, {
              member: node.member,
              showActions: !belongsToCurrentUser
            }, node.id);
          });
        })))
      });
    }))]
  });
};

var LikesList = function LikesList(_ref7) {
  var entityId = _ref7.entityId,
      totalCount = _ref7.totalCount;

  var _useToggle = useToggle(),
      _useToggle2 = _slicedToArray(_useToggle, 2),
      shown = _useToggle2[0],
      _useToggle2$ = _useToggle2[1],
      showDialog = _useToggle2$.on,
      hideDialog = _useToggle2$.off;

  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [shown ? /*#__PURE__*/_jsx(LikesDialog, {
      entityId: entityId,
      handleCloseDialog: hideDialog
    }) : null, /*#__PURE__*/_jsx(LikesButton, {
      showDialog: showDialog,
      totalCount: totalCount,
      "data-element": "LikesButton",
      "data-component": "LikesList",
      "data-source-file": "likes-list.tsx"
    })]
  });
};

export default LikesList;